import { type yup } from '@framework/validation/initYup.ts';
import { evaluateExpression } from '../schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '../schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../schema/expressions/references/collectExpressionReferences.ts';
import { type ICodeWidget } from '../schema/widgets/ICodeWidget.ts';
import {
  type IParentWidgetScope,
  type IWidgetType,
} from '../schema/widgets/IWidgetType.ts';
import { multiSelectOptionsObjectName } from '../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName.ts';
import {
  type IWidgetAdvancedOption,
  type IWidgetOptionValue,
  type Widget,
} from '../schema/widgets/Widget.ts';
import { remapBooleanConditionReferencesUpwardInScope } from './utils/remapReferencesUpwardInScope.ts';
import { WidgetConditionManager } from './WidgetConditionManager.ts';

/**
 * Applica la condizione di visibilità ad uno schema yup
 */
export function setVisibleCondition<W extends Widget, S extends yup.AnySchema>(
  schema: S,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope = [],
) {
  const conditionData = WidgetConditionManager.mergeWithScope(
    widget,
    'visible',
    parentScope,
  );

  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema
      : schema.when([], (_, schema) => schema.notRequired().strip());
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);

      const isVisible = evaluateExpression(condition.expression, { variables });

      if (isVisible) return schema;
      return schema.withMutation(s => s.notRequired().strip());
    },
  );
}

/**
 * Funzione che applica la condizione di visualizzazione allo schema
 * di una singola opzione di un widget.
 */
export function setOptionVisibleCondition<
  W extends ICodeWidget,
  S extends yup.AnySchema,
>(schema: S, widget: W, option: IWidgetOptionValue | IWidgetAdvancedOption) {
  const condition = remapBooleanConditionReferencesUpwardInScope(
    { code: multiSelectOptionsObjectName(widget.code) },
    option.visible,
  );

  if (!condition) return schema;
  if (condition.type === 'static') {
    return condition.value ? schema : schema.notRequired().strip();
  }

  const references = collectExpressionReferences(condition.expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);
    const isVisible = evaluateExpression(condition.expression, { variables });

    if (isVisible) return schema;
    return schema.withMutation((s: any) => {
      s.notRequired();
      s.strip();
      return s;
    });
  });
}
